import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { ChartContainer } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { BarChart3, Table2 } from "lucide-react";

export interface Serie {
  chave: string;
  rotulo: string;
  /** CSS color — use os tokens --viz-N, na ordem validada. */
  cor: string;
}

type Linha = Record<string, string | number | null>;

interface Props {
  dados: Linha[];
  chaveX: string;
  series: Serie[];
  formatarValor: (v: number) => string;
  /** Eixo Y compacto (ex.: "R$ 350 mil"); padrão = formatarValor. */
  formatarEixo?: (v: number) => string;
  formatarX?: (v: string) => string;
  rotuloX: string;
  /** false = colunas lado a lado (ex.: admissões × desligamentos). */
  empilhado?: boolean;
  /** Mostra a soma das séries no tooltip e na tabela. */
  mostrarTotal?: boolean;
  altura?: number;
}

const RAIO = 4;

/** Retângulo com só o topo arredondado (a base fica reta, presa ao eixo). */
function caminhoColuna(x: number, y: number, w: number, h: number, raio: number) {
  if (h <= 0 || w <= 0) return "";
  const r = Math.min(raio, w / 2, h);
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

/**
 * Colunas no padrão de visualização do app: no máximo 24px, topo arredondado
 * só no segmento de cima, 2px de vão na cor da superfície entre segmentos,
 * grade hairline, legenda sempre que houver mais de uma série, tooltip e uma
 * visão em tabela com os mesmos números.
 */
export const GraficoColunas = ({
  dados,
  chaveX,
  series,
  formatarValor,
  formatarEixo,
  formatarX = (v) => v,
  rotuloX,
  empilhado = true,
  mostrarTotal = false,
  altura = 260,
}: Props) => {
  const [tabela, setTabela] = useState(false);
  const valor = (l: Linha, chave: string) => Number(l[chave] ?? 0);
  const total = (l: Linha) => series.reduce((s, x) => s + valor(l, x.chave), 0);

  // O segmento é o "de cima" quando nenhuma série acima dele tem valor naquela coluna.
  const forma = (indice: number) => (props: unknown) => {
    const { x, y, width, height, payload, fill, stroke, strokeWidth } = props as {
      x: number;
      y: number;
      width: number;
      height: number;
      payload: Linha;
      fill: string;
      stroke?: string;
      strokeWidth?: number;
    };
    const topo = !empilhado || series.slice(indice + 1).every((s) => valor(payload, s.chave) <= 0);
    // O traço na cor da superfície é o vão de 2px entre segmentos, não uma borda.
    return (
      <path d={caminhoColuna(x, y, width, height, topo ? RAIO : 0)} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {series.length > 1 ? (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Legenda">
            {series.map((s) => (
              <li key={s.chave} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.cor }} aria-hidden />
                {s.rotulo}
              </li>
            ))}
          </ul>
        ) : (
          <span />
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setTabela((t) => !t)}
          aria-pressed={tabela}
        >
          {tabela ? <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> : <Table2 className="mr-1.5 h-3.5 w-3.5" />}
          {tabela ? "Ver gráfico" : "Ver tabela"}
        </Button>
      </div>

      {tabela ? (
        <div className="max-h-[320px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{rotuloX}</TableHead>
                {series.map((s) => (
                  <TableHead key={s.chave} className="text-right">
                    {s.rotulo}
                  </TableHead>
                ))}
                {mostrarTotal && <TableHead className="text-right">Total</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dados.map((l) => (
                <TableRow key={String(l[chaveX])}>
                  <TableCell className="capitalize">{formatarX(String(l[chaveX]))}</TableCell>
                  {series.map((s) => (
                    <TableCell key={s.chave} className="text-right tabular-nums">
                      {formatarValor(valor(l, s.chave))}
                    </TableCell>
                  ))}
                  {mostrarTotal && (
                    <TableCell className="text-right font-medium tabular-nums">{formatarValor(total(l))}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ChartContainer config={{}} className="aspect-auto w-full" style={{ height: altura }}>
          <BarChart data={dados} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="" />
            <XAxis
              dataKey={chaveX}
              tickFormatter={(v) => formatarX(String(v))}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--border))" }}
              interval={0}
              fontSize={11}
            />
            <YAxis
              tickFormatter={(v) => (formatarEixo ?? formatarValor)(Number(v))}
              tickLine={false}
              axisLine={false}
              width={76}
              fontSize={11}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted))", opacity: 0.6 }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const linha = payload[0].payload as Linha;
                return (
                  <div className="min-w-[180px] rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
                    <p className="mb-1 font-medium capitalize text-foreground">{formatarX(String(label))}</p>
                    {[...series].reverse().map((s) => (
                      <p key={s.chave} className="flex items-center justify-between gap-4 text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-sm" style={{ background: s.cor }} aria-hidden />
                          {s.rotulo}
                        </span>
                        <span className="tabular-nums text-foreground">{formatarValor(valor(linha, s.chave))}</span>
                      </p>
                    ))}
                    {mostrarTotal && (
                      <p className="mt-1 flex justify-between gap-4 border-t pt-1 font-medium text-foreground">
                        <span>Total</span>
                        <span className="tabular-nums">{formatarValor(total(linha))}</span>
                      </p>
                    )}
                  </div>
                );
              }}
            />
            {series.map((s, i) => (
              <Bar
                key={s.chave}
                dataKey={s.chave}
                name={s.rotulo}
                stackId={empilhado ? "a" : undefined}
                fill={s.cor}
                maxBarSize={24}
                stroke="hsl(var(--card))"
                strokeWidth={empilhado ? 2 : 0}
                shape={forma(i)}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
};

/** Bloco de número (stat tile): rótulo, valor e, opcionalmente, uma linha de apoio. */
export const Indicador = ({
  rotulo,
  valor,
  apoio,
  className,
}: {
  rotulo: string;
  valor: string;
  apoio?: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("rounded-lg border bg-card p-4", className)}>
    <p className="text-xs text-muted-foreground">{rotulo}</p>
    <p className="mt-1 text-xl font-semibold">{valor}</p>
    {apoio && <div className="mt-1 text-xs text-muted-foreground">{apoio}</div>}
  </div>
);
